/**
 * useAssembly.js — loads the static assembly manifest and builds the tree.
 *
 * Geometry truth comes from the static manifest only. The backend is used for
 * KG *enrichment* and is always optional: every network path here degrades to
 * an empty result rather than throwing, so the 3D view keeps working with the
 * API down.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildTree } from './assemblyTree.js';

export const DEFAULT_VEHICLE = 'ace_ev';
export const MANIFEST_PATH = 'cad/ace_ev_assembly.json';

/** Manifest path for a vehicle: 'ace_ev' -> 'cad/ace_ev_assembly.json'. */
export const manifestPathFor = (vehicle) => `cad/${vehicle || DEFAULT_VEHICLE}_assembly.json`;

/** Shape returned when the backend is unreachable or has nothing for a node. */
export function emptyNodeDetail(nodeId = null) {
  return {
    node_id: nodeId,
    matched_kg: [],
    specifications: [],
    failures: [],
    systems: [],
    procedures: [],
    unavailable: true,
  };
}

function apiBase() {
  const raw = (typeof import.meta !== 'undefined' && import.meta.env
    && import.meta.env.VITE_API_BASE_URL) || '';
  return String(raw).replace(/\/+$/, '');
}

function manifestUrl(vehicle) {
  const base = (typeof import.meta !== 'undefined' && import.meta.env
    && import.meta.env.BASE_URL) || '/';
  return `${base}${manifestPathFor(vehicle)}`;
}

function normalizeDetail(nodeId, data) {
  const arr = (v) => (Array.isArray(v) ? v : []);
  if (!data || typeof data !== 'object') return emptyNodeDetail(nodeId);
  return {
    node_id: data.node_id ?? nodeId,
    matched_kg: arr(data.matched_kg),
    specifications: arr(data.specifications),
    failures: arr(data.failures),
    systems: arr(data.systems),
    procedures: arr(data.procedures),
    unavailable: false,
  };
}

/**
 * @param {{vehicle?:string, url?:string}} [options]
 * @returns {{
 *   manifest: object|null,
 *   tree: object,
 *   loading: boolean,
 *   error: Error|null,
 *   reload: () => void,
 *   fetchNodeDetail: (nodeId:string) => Promise<object>,
 *   fetchBom: () => Promise<{vehicle:string, nodes:object[]}>
 * }}
 */
export function useAssembly(options = {}) {
  const vehicle = options.vehicle ?? DEFAULT_VEHICLE;
  const url = options.url ?? manifestUrl(vehicle);

  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // nodeId -> promise/result cache, so hovering the tree doesn't re-hit the API
  const detailCache = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`manifest ${res.status} ${res.statusText}`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        setManifest(json);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled || err?.name === 'AbortError') return;
        setManifest(null);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });

    return () => { cancelled = true; controller.abort(); };
  }, [url, reloadKey]);

  const tree = useMemo(() => buildTree(manifest ?? []), [manifest]);

  const reload = useCallback(() => {
    detailCache.current.clear();
    setReloadKey((k) => k + 1);
  }, []);

  const fetchNodeDetail = useCallback(async (nodeId) => {
    // Vehicles converted from external CAD have no knowledge graph behind them
    // (manifest declares kg:false). Asking the backend would 404 on every part
    // click; return the same empty shape the UI already renders calmly.
    if (manifest && manifest.kg === false) {
      return { node_id: nodeId, matched_kg: [], specifications: [],
               failures: [], systems: [], procedures: [], unavailable: true };
    }
    if (typeof nodeId !== 'string' || !nodeId) return emptyNodeDetail(nodeId ?? null);
    const cache = detailCache.current;
    if (cache.has(nodeId)) return cache.get(nodeId);

    const base = apiBase();
    if (!base) {
      const empty = emptyNodeDetail(nodeId);
      cache.set(nodeId, Promise.resolve(empty));
      return empty;
    }

    const p = (async () => {
      try {
        const res = await fetch(
          `${base}/assembly/${encodeURIComponent(vehicle)}/node/${encodeURIComponent(nodeId)}`,
        );
        if (!res.ok) return emptyNodeDetail(nodeId);
        return normalizeDetail(nodeId, await res.json());
      } catch {
        // Backend down / CORS / offline — never propagate, the 3D view must live.
        return emptyNodeDetail(nodeId);
      }
    })();

    cache.set(nodeId, p);
    const settled = await p;
    // Do not cache a failure permanently — let the next click retry.
    if (settled.unavailable) cache.delete(nodeId);
    else cache.set(nodeId, settled);
    return settled;
  }, [vehicle]);

  const fetchBom = useCallback(async () => {
    const base = apiBase();
    if (!base) return { vehicle, nodes: [], unavailable: true };
    try {
      const res = await fetch(`${base}/assembly/${encodeURIComponent(vehicle)}/bom`);
      if (!res.ok) return { vehicle, nodes: [], unavailable: true };
      const json = await res.json();
      return {
        vehicle: json?.vehicle ?? vehicle,
        nodes: Array.isArray(json?.nodes) ? json.nodes : [],
        unavailable: false,
      };
    } catch {
      return { vehicle, nodes: [], unavailable: true };
    }
  }, [vehicle]);

  return { manifest, tree, loading, error, reload, fetchNodeDetail, fetchBom };
}

export default useAssembly;


/**
 * Map knowledge-graph node names (from a /query reasoning_path) onto CAD part
 * ids — the chat -> 3D direction.
 *
 * Resolves to [] on any failure: an unreachable backend must never break the
 * chat, it just means the answer does not drive the model this time.
 */
export async function locateParts(vehicle, names, { signal } = {}) {
  const list = (names || []).filter(Boolean).slice(0, 200);
  if (list.length === 0) return [];
  const base = apiBase();
  if (!base) return [];
  try {
    const res = await fetch(`${base}/assembly/${encodeURIComponent(vehicle || DEFAULT_VEHICLE)}/locate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ names: list }),
      signal,
    });
    if (!res.ok) return [];
    const data = await res.json();
    const parts = (data?.parts || []).filter((p) => p && p.id);
    if (parts.length === 0) return [];
    // Relative cutoff. The endpoint returns everything that shares a token, but
    // a reasoning path legitimately wanders (a "battery cooling" answer really
    // does traverse Brake and Powertrain nodes), so highlighting the whole tail
    // would point at half the vehicle. Keep only what is close to the best
    // match — a tight, defensible highlight beats a broad suggestive one.
    const top = parts[0].score || 0;
    const cutoff = top * 0.5;
    return parts.filter((p) => (p.score || 0) >= cutoff).slice(0, 8).map((p) => p.id);
  } catch {
    return [];
  }
}
